export type NotesPerBar = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
export type BeatsPerBar = 4 | 8

/**
 * A time signature.
 *
 * ⚠️ The two field names are INVERTED with respect to what they hold — read them through
 * {@link meterTop} / {@link meterBottom}, never positionally:
 *
 * - `notesPerBar` is the **TOP** number — the number of beats in a bar (the 7 of 7/8).
 * - `beatsPerBar` is the **BOTTOM** number — the note value that gets one beat (the 8 of 7/8).
 *
 * The names are wrong for historical reasons and are kept deliberately: they are the persisted
 * DynamoDB attribute names, the shape is embedded in `meterSchema` (`@tunasong/schemas`), and
 * renaming them makes `isSong()` return false for every already-stored song that has a meter.
 * There is no entity-migration mechanism, so the rule is fix-at-the-use-site, never migrate
 * persisted user data.
 *
 * The inversion is not theoretical: it cost us a transposed `[beatsPerBar, notesPerBar]` pair
 * handed to Tone's `Transport.timeSignature`, which made every bar position, loop point, `"1m"`
 * duration and metronome bar wrong in any non-4/4 song — a bar of 3/4 ran 5.333 beats and 7/8 ran
 * 4.571. 4/4 is symmetric under the transposition, which is why it survived so long.
 */
export interface Meter {
  notesPerBar: NotesPerBar
  beatsPerBar: BeatsPerBar
}

/** The top number of the time signature — the number of beats in a bar (the 7 of 7/8). */
export const meterTop = (meter: Meter): NotesPerBar => meter.notesPerBar

/** The bottom number of the time signature — the note value that gets one beat (the 8 of 7/8). */
export const meterBottom = (meter: Meter): BeatsPerBar => meter.beatsPerBar

/** Parse meter - e.g., 4/4 or 7/8 */
export const parseMeter = (meter: string): Meter | null => {
  const meterRe = /^\s*(\d+)\s*\/\s*(\d+)\s*$/
  const res = meter.match(meterRe)
  if (!(res && res[0] && res[1] && res[2])) {
    return null
  }
  const notesPerBar = parseInt(res[1], 10) as NotesPerBar
  const beatsPerBar = parseInt(res[2], 10)
  if (beatsPerBar !== 4 && beatsPerBar !== 8) {
    return null
  }
  return { notesPerBar, beatsPerBar }
}

export const meterToString = (meter?: Meter) =>
  meter?.beatsPerBar && meter?.notesPerBar ? `${meterTop(meter)} / ${meterBottom(meter)}` : null
